import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms'
import { MasterCommonServices } from '../../quotation/master.common.service'
import { WorkOrderService } from '../workOrder.service'
import { ToastrService } from 'ngx-toastr'
import { Router } from '@angular/router'
import { AuthStateService } from '../../../auth-state.service'

@Component({
  selector: 'app-work-order-status',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './workOrderStatus.component.html',
  styleUrl: './workOrderStatus.component.css',
})
export class WorkOrderStatusComponent implements OnInit {
  workOrders: any[] = []
  form: FormGroup = new FormGroup({})
  highlightWoNumber: string | null = null
  filter = {
    woId: '',
    poId: '',
    workOrderCompany: '',
    invoiceStatus: '',
    invoiceValue: '', 
    stickerStatus: '', 
    stickerValue: '', 
    tcStatus: '', 
    tcValue: '', 
    operationsStatus: '', 
    operationsValue: '', 
    remarks: '',
  }

  filteredWorkOrders: any[] = []
  constructor(
    private fb: FormBuilder,
    public workOrderCommonServices: MasterCommonServices,
    public workOrderService: WorkOrderService,
    public toastr: ToastrService,
    private router: Router,
    private authStateService: AuthStateService,
  ) {
    const navigation = this.router.getCurrentNavigation()
    if (navigation?.extras?.state) {
      this.highlightWoNumber = navigation.extras.state['highlightWoNumber']
      
    }
  }

  permissions: string[] = []

  ngOnInit(): void {
    this.authStateService.permissions.subscribe((permList) => {
      this.permissions = permList
    })

    if (this.workOrderCommonServices.clientsList.length === 0) {
      this.workOrderCommonServices.getAllClients()
    }

    this.loadWorkOrders()
  }

  get canEditInvoice(): boolean {
    return this.permissions.includes('WorkOrdersStatusInvoicePermission')
  }
  get canEditSticker(): boolean {
    return this.permissions.includes('WorkOrdersStatusStickerPermission')
  }
  get canEditTC(): boolean {
    return this.permissions.includes('WorkOrdersStatusTCPermission')
  }
  get canEditOperations(): boolean {
    return this.permissions.includes('WorkOrdersStatusOperationsPermission')
  }

  async loadWorkOrders(): Promise<void> {
    this.workOrderService.getAllWorkOrdersStatus().subscribe((workOrders) => {
      this.workOrders = workOrders || []

      this.form = this.fb.group({
        workOrderArray: this.fb.array(
          this.workOrders.map((wo) =>
            this.fb.group({
              invoiceUpdatedBy: [wo.invoiceUpdatedBy || ''],
              stickerUpdatedBy: [wo.stickerUpdatedBy || ''],
              tcUpdatedBy: [wo.tcUpdatedBy || ''],
              operationsUpdatedBy: [wo.operationsUpdatedBy || ''],
              remarks: [wo.remarks || ''],
              recordVersion: [wo.recordVersion || 0],
              woRef: [wo],
            }),
          ),
        ),
      })

      if (this.highlightWoNumber) {
        setTimeout(() => {
          const row = document.querySelector(
            `tr[data-wo="${this.highlightWoNumber}"]`,
          )
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 200)
      }
      this.filteredWorkOrders = [...this.workOrders]
      setTimeout(() => this.resizeAllTextareas(), 0)
    })
  }

  saveStatus(wo: any): void {
    wo.modifiedOn = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    ).toISOString()

    const processedData = this.processData({ ...wo })
    this.workOrderService.updateWorkOrderStatus(processedData).subscribe({
      next: () => {
        this.toastr.success('Work Order Updated Successfully!', 'Success')
        this.loadWorkOrders()
        this.resetFilters()
      },
      error: (error: any) => {
        this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
      },
    })
  }

  private processData(obj: any): any {
    const cleaned: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key]
        cleaned[key] =
          typeof value === 'string' && value.trim() === '' ? null : value
      }
    }
    return cleaned
  }

  applyFilters(): void {
    this.filteredWorkOrders = this.workOrders.filter((wo) => {
      const clientName =
        this.workOrderCommonServices
          ?.getClientName(wo.workOrderCompanyId)
          ?.companyName?.toLowerCase() || ''

      const workOrderNumber = (wo.workOrderNumber || '')
        .toString()
        .toLowerCase()
      const purchaseOrderNumber = (wo.purchaseOrderNumber || '')
        .toString()
        .toLowerCase()
      const remarks = (wo.remarks || '').toString().toLowerCase()

      const invoice = (wo.invoiceUpdatedBy || '').toString().toLowerCase()
      const sticker = (wo.stickerUpdatedBy || '').toString().toLowerCase()
      const tc = (wo.tcUpdatedBy || '').toString().toLowerCase()
      const operations = (wo.operationsUpdatedBy || '').toString().toLowerCase()

      const matchesTextFilter = (value: string, filterVal?: string) =>
        !filterVal || value.includes(filterVal.toLowerCase())

      const matchesStatusFilter = (
        fieldValue: string,
        status: string,
        valueFilter?: string,
      ) => {
        if (!status) return true

        if (status === 'NoValue') {
          return !fieldValue?.trim()
        }

        if (status === 'WithValue') {
          return valueFilter
            ? fieldValue.includes(valueFilter.toLowerCase())
            : !!fieldValue?.trim()
        }

        return true
      }

      return (
        matchesTextFilter(workOrderNumber, this.filter.woId) &&
        matchesTextFilter(purchaseOrderNumber, this.filter.poId) &&
        matchesTextFilter(clientName, this.filter.workOrderCompany) &&
        matchesStatusFilter(
          invoice,
          this.filter.invoiceStatus,
          this.filter.invoiceValue,
        ) &&
        matchesStatusFilter(
          sticker,
          this.filter.stickerStatus,
          this.filter.stickerValue,
        ) &&
        matchesStatusFilter(tc, this.filter.tcStatus, this.filter.tcValue) &&
        matchesStatusFilter(
          operations,
          this.filter.operationsStatus,
          this.filter.operationsValue,
        ) &&
        matchesTextFilter(remarks, this.filter.remarks)
      )
    })
  }

  resetFilters(): void {
    this.filter = {
      woId: '',
      poId: '',
      workOrderCompany: '',
      invoiceStatus: '',
      invoiceValue: '',
      stickerStatus: '',
      stickerValue: '',
      tcStatus: '',
      tcValue: '',
      operationsStatus: '',
      operationsValue: '',
      remarks: '',
    }
    this.filteredWorkOrders = [...this.workOrders]
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeAllTextareas())
  }

  resizeAllTextareas(): void {
    const textareas = document.querySelectorAll('textarea.auto-resize')
    textareas.forEach((textarea: any) => {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    })
  }

   autoResize(event: any) {
    event.target.style.height = 'auto'
    event.target.style.height = event.target.scrollHeight + 'px'
  }
}
